import api from "./client";

// Dispatch board round 2 — RT's legs on crm-mk2 `lane-rt/sched-customer-card`,
// feature-detected until E deploys them. Each reader answers null when the API
// has no such route, and the board degrades to what it has (straight lines, no
// Refresh button) instead of erroring.
//
//   POST /api/maps/directions/            { stops:[{lat,lng,id?,label?}] (ordered, 2..60),
//                                           technician_id?, date?, refresh? }
//     → { provider, polyline, legs:[{from_index,to_index,duration_seconds,distance_meters,polyline}],
//         total_duration_seconds, total_distance_meters, cached }
//   POST /api/scheduling/schedules/dispatch-refresh/  { day, wait_seconds? }
//     → 200 {status:"completed"|"failed", run, day_sync} · 202 {status:"timeout", day_sync}
//       · 200 {status:"not_fieldroutes"} · 503 enqueue_failed
//   GET  /api/integrations/               [{ provider:"fieldroutes", enabled, writeback:{route_assignment} }]
//   board payload: day_sync {date,last_synced_at,source,status:"synced"|"stale"|"never",stale,age_seconds,is_fieldroutes}

// RT's directions proxy and dispatch-refresh ship in the same backend deploy as the
// board's `day_sync` block, so the board payload is the feature flag: the host
// calls markLegsFromBoard(board) after each load and nothing is probed. (An
// OPTIONS probe is wrong here twice over: the schedules router answers OPTIONS for
// ANY <pk>, so dispatch-refresh "existed" before it did, and a 404 probe is a
// console error on every board load.)
const present = { [DIRECTIONS_PATH_KEY()]: false, [REFRESH_PATH_KEY()]: false };
function DIRECTIONS_PATH_KEY() { return "/maps/directions/"; }
function REFRESH_PATH_KEY() { return "/scheduling/schedules/dispatch-refresh/"; }

export function markLegsFromBoard(board) {
  const live = Boolean(board && typeof board === "object" && board.day_sync && typeof board.day_sync === "object");
  present[DIRECTIONS_PATH_KEY()] = live;
  present[REFRESH_PATH_KEY()] = live;
  return live;
}

export const DIRECTIONS_PATH = DIRECTIONS_PATH_KEY();
export const REFRESH_PATH = REFRESH_PATH_KEY();

export const directionsAvailable = async () => present[DIRECTIONS_PATH];
export const refreshAvailable = async () => present[REFRESH_PATH];

/** Road-routed directions for one tech's ordered stops, or null when the proxy is not live. */
export async function fetchDirections(stops, { technicianId, date, refresh = false } = {}) {
  if (!Array.isArray(stops) || stops.length < 2) return null;
  if (!(await directionsAvailable())) return null;
  try {
    const { data } = await api.post(DIRECTIONS_PATH, {
      stops: stops.slice(0, 60).map((s) => ({ lat: Number(s.lat), lng: Number(s.lng), id: s.id != null ? String(s.id) : undefined, label: s.label })),
      technician_id: technicianId || undefined,
      date: date || undefined,
      refresh: refresh || undefined,
    });
    return data || null;
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404) { present[DIRECTIONS_PATH] = false; return null; }
    // 429 (daily cap) / 503 (budget) / 422 (unroutable): the map falls back to straight lines and says why.
    return { error: e?.response?.data?.error || e?.response?.data?.detail || "directions_unavailable", status };
  }
}

/** Ask the worker to re-pull one day from FieldRoutes and wait for it (≤ 55 s). null when the route is absent. */
export async function dispatchRefresh(day, { waitSeconds = 40 } = {}) {
  if (!(await refreshAvailable())) return null;
  try {
    const res = await api.post(REFRESH_PATH, { day, wait_seconds: waitSeconds }, { timeout: (waitSeconds + 15) * 1000 });
    return { ...res.data, http: res.status };
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404) { present[REFRESH_PATH] = false; return null; }
    return { status: e?.response?.data?.status || "failed", http: status, detail: e?.response?.data?.detail, run: e?.response?.data?.run, day_sync: e?.response?.data?.day_sync };
  }
}

/** FieldRoutes writeback flags for this tenant: { fieldroutes, routeAssignment } (nulls when unknown). */
export async function fetchWritebackFlags() {
  try {
    const { data } = await api.get("/integrations/");
    const rows = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    const fr = rows.find((r) => String(r.provider || r.kind || "").toLowerCase() === "fieldroutes");
    if (!fr) return { fieldroutes: false, routeAssignment: false, known: true };
    const wb = fr.writeback && typeof fr.writeback === "object" ? fr.writeback : {};
    return { fieldroutes: Boolean(fr.enabled), routeAssignment: Boolean(wb.route_assignment), known: true };
  } catch {
    return { fieldroutes: null, routeAssignment: null, known: false };
  }
}

/** Google's encoded polyline → [{lat,lng}]. The proxy encodes with the same algorithm whichever provider answered. */
export function decodePolyline(encoded) {
  const out = [];
  if (!encoded) return out;
  let index = 0; let lat = 0; let lng = 0;
  while (index < encoded.length) {
    let shift = 0; let result = 0; let b;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    out.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return out;
}

/** "Synced from FieldRoutes 12 min ago" — from day_sync, else the older appointment_freshness. */
export function freshnessOf(board) {
  const ds = board?.day_sync;
  const af = board?.appointment_freshness;
  if (ds && typeof ds === "object" && (ds.is_fieldroutes || ds.last_synced_at)) {
    const age = Number.isFinite(Number(ds.age_seconds)) ? Number(ds.age_seconds) : ds.last_synced_at ? (Date.now() - new Date(ds.last_synced_at).getTime()) / 1000 : null;
    return { isFieldroutes: Boolean(ds.is_fieldroutes), status: ds.status || (ds.last_synced_at ? "synced" : "never"), lastSyncedAt: ds.last_synced_at || null, ageSeconds: age, source: ds.source || "", perDay: true };
  }
  if (af && typeof af === "object" && af.is_fieldroutes) {
    const age = af.last_synced_at ? (Date.now() - new Date(af.last_synced_at).getTime()) / 1000 : null;
    return { isFieldroutes: true, status: af.stale ? "stale" : af.last_synced_at ? "synced" : "never", lastSyncedAt: af.last_synced_at || null, ageSeconds: age, source: "", perDay: false };
  }
  return { isFieldroutes: false, status: "", lastSyncedAt: null, ageSeconds: null, source: "", perDay: false };
}

export const ageWords = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds)) return "";
  const m = Math.round(seconds / 60);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = m / 60;
  if (h < 24) return `${h < 10 ? h.toFixed(1).replace(/\.0$/, "") : Math.round(h)} h ago`;
  return `${Math.round(h / 24)} d ago`;
};
